/**
 * PendingApprovalOverlay: the holding screen.
 *
 * Two things put it up, both as a 403 with code PENDING_APPROVAL that
 * apiFetch turns into a `pidly:pending-approval` event:
 *
 *   - scope 'account': the person's own account is waiting for pidly's
 *     approval (a self-serve sign-up, middleware/auth.js). They can sign
 *     in and that is all, so the screen covers the whole app. It asks
 *     /api/auth/me every half minute and reloads the moment the account
 *     is approved; the screen says nothing about that, it just happens.
 *   - scope 'workspace': the person is a member of a project whose
 *     membership policy needs an owner's approval (orgRoutes).
 *
 * The account case is also checked on load: the stored user carries
 * approvalStatus, so a refresh shows the screen at once instead of after
 * the first refused request. Not on the sign-in pages: those are open to
 * a pending person by design.
 *
 * Drawn as the app's ONE dialog (utils/dialogs.js, the `pdlg-` classes),
 * so it follows the theme like every confirm: Light Band for a new
 * account, whatever the person picks later.
 *
 * Lives at the app root (mounted in App.jsx, outside Router) so any page
 * gets the same screen.
 */

import { useEffect, useRef, useState } from 'react';
import apiFetch from '../utils/apiFetch';
import { ensureDialogStyles } from '../utils/dialogs';
import { clearAuth, getAuthToken, getStoredUser, setStoredUser } from '../utils/authStorage';

const AUTH_FLOW_PATHS = ['/login', '/verify', '/reset-password', '/join', '/confirm-email'];
const POLL_MS = 30 * 1000;

function onAuthFlowPage() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return AUTH_FLOW_PATHS.some(p => path === p || path.startsWith(p + '/'));
}

export default function PendingApprovalOverlay() {
  const [scope, setScope] = useState(null); // null (closed) | 'account' | 'workspace'
  const [orgName, setOrgName] = useState('');
  const checking = useRef(false);

  // Ask the server whether the account is still pending; when it is not,
  // store the fresh user and reload so the app opens.
  const checkAccount = async ({ openIfPending }) => {
    if (checking.current) return;
    checking.current = true;
    try {
      const r = await apiFetch('/api/auth/me');
      if (r.status === 401) return; // signed out meanwhile: the app handles it
      const d = await r.json().catch(() => null);
      if (!d?.user) return;
      try { setStoredUser(d.user); } catch { /* storage unavailable */ }
      if (d.user.approvalStatus === 'pending') {
        if (openIfPending) setScope('account');
      } else if (scope === 'account') {
        window.location.reload();
      }
    } catch { /* offline: try again next time */ }
    finally { checking.current = false; }
  };

  useEffect(() => {
    ensureDialogStyles();
    const onPending = (e) => {
      const detail = e?.detail || {};
      if (detail.scope === 'account') { setScope('account'); return; }
      setScope(s => s || 'workspace');
      const orgId = detail.orgId;
      if (orgId) {
        try {
          const cached = JSON.parse(sessionStorage.getItem('pidly.lastOrgName') || 'null');
          if (cached && cached.id === orgId) setOrgName(cached.name || '');
        } catch { /* nothing cached */ }
      }
    };
    window.addEventListener('pidly:pending-approval', onPending);

    // On load: a stored pending user shows the screen at once, and the
    // server is asked in case the approval came while the tab was shut.
    if (getAuthToken() && !onAuthFlowPage()) {
      if (getStoredUser()?.approvalStatus === 'pending') setScope('account');
      checkAccount({ openIfPending: true });
    }
    return () => window.removeEventListener('pidly:pending-approval', onPending);
  }, []);

  // While the account screen is up, look for the approval.
  useEffect(() => {
    if (scope !== 'account') return undefined;
    const id = setInterval(() => checkAccount({ openIfPending: false }), POLL_MS);
    return () => clearInterval(id);
  }, [scope]);

  if (!scope) return null;

  const handleSignOut = () => {
    try { clearAuth(); } catch { /* nothing to clear */ }
    if (typeof window !== 'undefined') window.location.href = '/login';
  };
  const handleRefresh = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  const account = scope === 'account';

  return (
    <div className="pdlg-overlay">
      <div className="pdlg pidly-dialog-box" role="dialog" aria-modal="true" aria-labelledby="pidly-pending-title">
        <h2 id="pidly-pending-title" className="pdlg-title">Awaiting approval</h2>
        <div className="pdlg-body">
          {account
            ? 'Your account is waiting for pidly to approve it. You will get an email when it is ready.'
            : `Your membership of ${orgName || 'this project'} is waiting for an owner to approve it.`}
        </div>
        <div className="pdlg-foot">
          <button type="button" className="pdlg-btn pdlg-btn--quiet" onClick={handleSignOut}>Sign out</button>
          {!account && <button type="button" className="pdlg-btn pdlg-btn--primary" onClick={handleRefresh}>Refresh</button>}
        </div>
      </div>
    </div>
  );
}
